/**
 * The invoice import's printed-total check (FR-WORD).
 *
 * The parser reads three figures off the paper: subtotal, tax and total. What
 * the paper adds beyond subtotal and tax (freight, fuel, deposits) is not read
 * on its own, so it is worked out here as the printed total less the printed
 * subtotal and tax. The check then asks one thing: do the lines that are going
 * onto the order, plus the printed tax and charges, add up to the printed
 * total? Lines that are held back or unmatched are not part of the order, so
 * they never count against a correct invoice; they are reported on the side so
 * a gap they explain can be named.
 *
 * Mirrors the frozen Svelte screen's cascade: lines against the printed
 * subtotal; failing that, the printed total less tax; failing that, the
 * printed total.
 */

import { money } from '@/lib/format';
import { dollarsToCents } from './api';

export interface InvoiceTotalsInput {
  /** Lines going onto the order, qty × unit cost, in cents. */
  includedCents: number;
  /** Lines held back or unmatched, in cents. Never part of the check. */
  heldBackCents: number;
  /** What the parser read off the invoice, in cents; null when it found none. */
  printedSubtotalCents: number | null;
  printedTaxCents: number | null;
  printedTotalCents: number | null;
}

export type TotalsVerdict = 'matches' | 'close' | 'off' | 'unknown';

export interface InvoiceTotalsCheck {
  includedCents: number;
  heldBackCents: number;
  printedSubtotalCents: number | null;
  printedTaxCents: number | null;
  printedTotalCents: number | null;
  /** Printed total less printed subtotal and tax; null unless both were read. */
  chargesCents: number | null;
  /** The included lines plus printed tax and charges. */
  expectedTotalCents: number;
  /** What the lines are being compared with, in words. */
  comparedWith: 'the invoice total' | 'the invoice subtotal' | null;
  /** Included lines side minus the printed side; + means the lines are over. */
  differenceCents: number | null;
  verdict: TotalsVerdict;
  /** The held-back lines, when adding them would close the gap. */
  heldBackExplainsGap: boolean;
}

/** More than this many cents apart is a real mismatch, not rounding. */
export const TOTALS_TOLERANCE_CENTS = 100;

export function checkInvoiceTotals(input: InvoiceTotalsInput): InvoiceTotalsCheck {
  const { includedCents, heldBackCents } = input;
  const subtotal = input.printedSubtotalCents;
  const tax = input.printedTaxCents;
  const total = input.printedTotalCents;

  const chargesCents =
    subtotal !== null && total !== null ? total - subtotal - (tax ?? 0) : null;
  const expectedTotalCents = includedCents + (tax ?? 0) + (chargesCents ?? 0);

  let comparedWith: InvoiceTotalsCheck['comparedWith'] = null;
  let differenceCents: number | null = null;
  if (total !== null) {
    comparedWith = 'the invoice total';
    differenceCents = expectedTotalCents - total;
  } else if (subtotal !== null) {
    comparedWith = 'the invoice subtotal';
    differenceCents = includedCents - subtotal;
  }

  const verdict: TotalsVerdict =
    differenceCents === null
      ? 'unknown'
      : differenceCents === 0
        ? 'matches'
        : Math.abs(differenceCents) <= TOTALS_TOLERANCE_CENTS
          ? 'close'
          : 'off';

  const heldBackExplainsGap =
    differenceCents !== null &&
    verdict === 'off' &&
    heldBackCents > 0 &&
    Math.abs(differenceCents + heldBackCents) <= TOTALS_TOLERANCE_CENTS;

  return {
    includedCents,
    heldBackCents,
    printedSubtotalCents: subtotal,
    printedTaxCents: tax,
    printedTotalCents: total,
    chargesCents,
    expectedTotalCents,
    comparedWith,
    differenceCents,
    verdict,
    heldBackExplainsGap,
  };
}

/** The tile under "Invoice says": how the lines stand against the paper. */
export function totalsSummary(check: InvoiceTotalsCheck): string {
  if (check.differenceCents === null) return 'no total read';
  if (check.differenceCents === 0) return 'matches the lines';
  const over = check.differenceCents > 0;
  return `lines ${over ? 'over' : 'under'} by ${money(Math.abs(check.differenceCents))}`;
}

/**
 * One sentence that shows the sum: the lines going onto the order, plus the
 * printed tax and charges, against what the paper says. Empty when the parser
 * read nothing to compare with.
 */
export function totalsExplanation(check: InvoiceTotalsCheck): string {
  if (check.comparedWith === null || check.differenceCents === null) return '';
  const target =
    check.comparedWith === 'the invoice total'
      ? check.printedTotalCents
      : check.printedSubtotalCents;
  const parts = [`lines on the order ${money(check.includedCents)}`];
  if (check.comparedWith === 'the invoice total') {
    if (check.printedTaxCents) parts.push(`tax ${money(check.printedTaxCents)}`);
    if (check.chargesCents) parts.push(`charges ${money(check.chargesCents)}`);
  }
  const sum = parts.join(' + ');
  const sentence =
    parts.length > 1
      ? `${sum} = ${money(check.expectedTotalCents)}, against ${check.comparedWith} ${money(target)}.`
      : `${sum}, against ${check.comparedWith} ${money(target)}.`;
  const hint = check.heldBackExplainsGap
    ? ` The ${money(check.heldBackCents)} held back or unmatched closes the gap.`
    : '';
  return `${sentence[0]!.toUpperCase()}${sentence.slice(1)}${hint}`;
}

/**
 * A figure the parser read off the invoice, in cents, or null when it read
 * none. A total or subtotal of zero is a failed read, not an invoice for
 * nothing; tax of zero is a real read (most grocery invoices carry none).
 */
export function printedCents(
  dollars: number | null | undefined,
  zeroIsRead = false
): number | null {
  if (dollars === null || dollars === undefined || !Number.isFinite(dollars)) return null;
  const cents = dollarsToCents(dollars);
  return cents === 0 && !zeroIsRead ? null : cents;
}
